import { apiConfig } from '@nomiss/configs';
import { buildApiHeaders, fetchWithAuthRecovery } from './sdk-client';
import { ApiRequestError } from './api-request.util';

export class AdminDataSyncError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'AdminDataSyncError');
  }
}

export type DataSyncMetrics = {
  staleSports: unknown[];
};

export type TriggerSyncResult = {
  jobId: string;
  status: string;
};

export type ReplayDlqResult = {
  message: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

async function handleResponse<T>(
  response: Response,
  fallback: string,
): Promise<T> {
  if (!response.ok) {
    const json = (await response.json().catch(() => ({}))) as {
      message?: string | string[];
    };
    const rawMessage = json.message;
    const message = Array.isArray(rawMessage)
      ? rawMessage.join(', ')
      : typeof rawMessage === 'string'
        ? rawMessage
        : fallback;
    throw new AdminDataSyncError(response.status, message || fallback);
  }

  const json = (await response.json()) as unknown;
  const envelope = isRecord(json) ? json : {};
  return (envelope.data !== undefined ? envelope.data : envelope) as T;
}

export type DataSyncQueueCounts = {
  waiting: number;
  inFlight: number;
  delayed: number;
};

function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return fallback;
}

async function getIngestionQueueDepth(
  queueNames: ReadonlySet<string>,
): Promise<number> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/data-sync/queues`,
    {
      method: 'GET',
      headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
    },
  );

  const result = await handleResponse<unknown>(
    response,
    'Failed to load data-sync queues',
  );
  const rows = Array.isArray(result)
    ? result
    : isRecord(result) && Array.isArray(result['data'])
      ? result['data']
      : [];

  const matched = rows.find((row) => {
    if (!isRecord(row)) {
      return false;
    }
    return (
      typeof row['queueName'] === 'string' && queueNames.has(row['queueName'])
    );
  });
  const counts =
    isRecord(matched) && isRecord(matched['counts']) ? matched['counts'] : {};

  return (
    asNumber(counts['waiting']) +
    asNumber(counts['inFlight']) +
    asNumber(counts['delayed'])
  );
}

export async function getDataSyncQueueDepth(): Promise<number> {
  return getIngestionQueueDepth(new Set(['data-sync-queue', 'data-sync']));
}

export async function getMatchIngestQueueDepth(): Promise<number> {
  return getIngestionQueueDepth(
    new Set(['match-ingest-queue', 'match-ingest']),
  );
}

export async function getSerpScrapeQueueDepth(): Promise<number> {
  return getIngestionQueueDepth(new Set(['serp-scrape-queue', 'serp-scrape']));
}

async function readMonitoringQueueBusy(
  path: string,
  fallback: string,
): Promise<boolean> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/monitoring/${path}`,
    {
      method: 'GET',
      headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
    },
  );

  const result = await handleResponse<unknown>(response, fallback);
  return isRecord(result) && result.queueBusy === true;
}

async function waitForMonitoringQueueIdle(options: {
  path: string;
  fallback: string;
  timeoutMessage: string;
  onProgress?: () => void;
}): Promise<void> {
  const timeoutMs = 30 * 60 * 1000;
  const pollMs = 1500;
  const stableMs = 3000;
  const minWaitMs = 2500;
  const idleWithoutWorkMs = 8000;
  const startedAt = Date.now();
  let sawWork = false;
  let idleSince: number | null = null;
  let lastProgressAt = 0;

  while (Date.now() - startedAt < timeoutMs) {
    const queueBusy = await readMonitoringQueueBusy(
      options.path,
      options.fallback,
    );
    if (queueBusy) {
      sawWork = true;
      idleSince = null;
    } else if (Date.now() - startedAt >= minWaitMs) {
      const canFinish = sawWork || Date.now() - startedAt >= idleWithoutWorkMs;
      if (canFinish) {
        if (idleSince === null) {
          idleSince = Date.now();
        } else if (Date.now() - idleSince >= stableMs) {
          return;
        }
      }
    }

    if (
      options.onProgress !== undefined &&
      Date.now() - lastProgressAt >= 5000
    ) {
      lastProgressAt = Date.now();
      options.onProgress();
    }

    await new Promise((resolve) => {
      setTimeout(resolve, pollMs);
    });
  }

  throw new AdminDataSyncError(408, options.timeoutMessage);
}

export async function waitForCatalogSyncIdle(options?: {
  onProgress?: () => void;
}): Promise<void> {
  return waitForMonitoringQueueIdle({
    path: 'catalog-sync',
    fallback: 'Failed to load catalog sync',
    timeoutMessage: 'Timed out waiting for catalog sync to finish',
    ...(options?.onProgress !== undefined
      ? { onProgress: options.onProgress }
      : {}),
  });
}

export async function waitForMatchIngestIdle(options?: {
  onProgress?: () => void;
}): Promise<void> {
  return waitForMonitoringQueueIdle({
    path: 'match-ingest',
    fallback: 'Failed to load match ingest',
    timeoutMessage: 'Timed out waiting for match ingest to finish',
    ...(options?.onProgress !== undefined
      ? { onProgress: options.onProgress }
      : {}),
  });
}

export async function waitForSerpHighlightsIdle(options?: {
  onProgress?: () => void;
}): Promise<void> {
  return waitForMonitoringQueueIdle({
    path: 'serp-highlights',
    fallback: 'Failed to load OTT highlights',
    timeoutMessage: 'Timed out waiting for OTT highlight scrape to finish',
    ...(options?.onProgress !== undefined
      ? { onProgress: options.onProgress }
      : {}),
  });
}

function parseTriggerResult(value: unknown): TriggerSyncResult {
  const data = isRecord(value) ? value : {};
  const jobId =
    typeof data.jobId === 'string' && data.jobId !== ''
      ? data.jobId
      : typeof data.messageId === 'string'
        ? data.messageId
        : '';
  return {
    jobId,
    status: typeof data.status === 'string' ? data.status : '',
  };
}

export async function triggerCatalogSync(
  force = true,
): Promise<TriggerSyncResult> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/data-sync/catalog`,
    {
      method: 'POST',
      headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ force }),
    },
  );

  return handleResponse<TriggerSyncResult>(
    response,
    'Failed to trigger catalog sync',
  );
}

export async function triggerCatalogSportSync(
  sportCode: string,
  force = true,
): Promise<TriggerSyncResult> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/data-sync/catalog/${encodeURIComponent(sportCode)}`,
    {
      method: 'POST',
      headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ force }),
    },
  );

  return handleResponse<TriggerSyncResult>(
    response,
    `Failed to trigger ${sportCode} catalog sync`,
  );
}

export async function triggerMatchIngest(
  force = true,
): Promise<TriggerSyncResult> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/data-sync/match-ingest`,
    {
      method: 'POST',
      headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ force }),
    },
  );

  const result = await handleResponse<unknown>(
    response,
    'Failed to trigger match ingest',
  );
  return parseTriggerResult(result);
}

export async function triggerMatchIngestSport(
  sportCode: string,
  force = true,
): Promise<TriggerSyncResult> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/data-sync/match-ingest`,
    {
      method: 'POST',
      headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ force, sportCode }),
    },
  );

  const result = await handleResponse<unknown>(
    response,
    `Failed to trigger ${sportCode} match ingest`,
  );
  return parseTriggerResult(result);
}

export async function triggerSerpHighlights(): Promise<TriggerSyncResult> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/data-sync/serp-highlights`,
    {
      method: 'POST',
      headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({}),
    },
  );

  const result = await handleResponse<unknown>(
    response,
    'Failed to trigger OTT highlight scrape',
  );
  return parseTriggerResult(result);
}

export async function triggerSerpHighlightsSport(
  sportCode: string,
): Promise<TriggerSyncResult> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/data-sync/serp-highlights`,
    {
      method: 'POST',
      headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ sportCode }),
    },
  );

  const result = await handleResponse<unknown>(
    response,
    `Failed to trigger ${sportCode} OTT highlight scrape`,
  );
  return parseTriggerResult(result);
}

export async function triggerFootballSync(
  force?: boolean,
): Promise<TriggerSyncResult> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/data-sync/football/trigger`,
    {
      method: 'POST',
      headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ force: force ?? false }),
    },
  );

  return handleResponse<TriggerSyncResult>(
    response,
    'Failed to trigger football sync',
  );
}

export async function triggerLeagueSync(
  leagueCode: string,
  force?: boolean,
): Promise<unknown> {
  const url = new URL(
    `${apiConfig.baseUrl}/v1/admin/data-sync/football/league/${encodeURIComponent(leagueCode)}`,
  );
  if (force) url.searchParams.set('force', 'true');

  const response = await fetchWithAuthRecovery(url.toString(), {
    method: 'POST',
    headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
  });

  return handleResponse<unknown>(response, 'Failed to trigger league sync');
}

export async function getDataSyncMetrics(): Promise<DataSyncMetrics> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/data-sync/metrics`,
    {
      method: 'GET',
      headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
    },
  );

  const result = await handleResponse<unknown>(
    response,
    'Failed to load data sync metrics',
  );

  const data = isRecord(result) ? result : {};
  return {
    staleSports: asArray(data.staleSports),
  };
}

export async function replayDlqJob(jobId: string): Promise<ReplayDlqResult> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/data-sync/dlq/replay`,
    {
      method: 'POST',
      headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ jobId }),
    },
  );

  const result = await handleResponse<unknown>(
    response,
    'Failed to replay DLQ job',
  );

  const data = isRecord(result) ? result : {};
  return {
    message: asString(data.message, 'Job replayed'),
  };
}
